import { useEffect, useRef, useState, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import { Terminal, Trash2, ArrowDown, Pause, Play, Wifi, WifiOff, Download } from 'lucide-react';
import type { EdgeLinkLogEntry } from '@/hooks/useEdgeLinkLogs';

interface LogViewerProps {
  logs: EdgeLinkLogEntry[];
  isConnected: boolean;
  /** Compact mode for use in modals - smaller height, fewer controls */
  compact?: boolean;
  /** Callback to clear logs (if not provided, clear button is hidden) */
  onClear?: () => void;
  /** Maximum height of the log container */
  maxHeight?: string;
}

function formatTimestamp(timestampUnixMs: number): string {
  const date = new Date(timestampUnixMs);
  return date.toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  });
}

function getLevelStyles(level: EdgeLinkLogEntry['level']) {
  switch (level) {
    case 'Error':
      return {
        text: 'text-red-400',
        bg: 'bg-red-500/10',
        border: 'border-red-500/30',
      };
    case 'Warn':
      return {
        text: 'text-yellow-400',
        bg: 'bg-yellow-500/10',
        border: 'border-yellow-500/30',
      };
    case 'Info':
      return {
        text: 'text-white',
        bg: 'bg-zinc-500/10',
        border: 'border-zinc-500/30',
      };
    case 'Debug':
      return {
        text: 'text-zinc-500',
        bg: 'bg-zinc-700/10',
        border: 'border-zinc-700/30',
      };
    default:
      return {
        text: 'text-zinc-400',
        bg: 'bg-zinc-600/10',
        border: 'border-zinc-600/30',
      };
  }
}

function LogEntryRow({ entry, compact }: { entry: EdgeLinkLogEntry; compact?: boolean }) {
  const styles = getLevelStyles(entry.level);

  if (compact) {
    return (
      <div className="flex items-start gap-2 py-0.5 font-mono text-xs">
        <span className="text-zinc-500 shrink-0">{formatTimestamp(entry.timestampUnixMs)}</span>
        <span className={cn('flex-1 break-all', styles.text)}>
          [{entry.source}] {entry.message}
        </span>
      </div>
    );
  }

  return (
    <div className="flex items-start gap-3 py-1.5 px-2 hover:bg-zinc-800/30 rounded font-mono text-sm">
      <span className="text-zinc-500 shrink-0 w-20">{formatTimestamp(entry.timestampUnixMs)}</span>
      <Badge
        variant="outline"
        className={cn(
          'shrink-0 w-14 justify-center text-[10px] font-semibold uppercase',
          styles.bg,
          styles.border,
          styles.text
        )}
      >
        {entry.level}
      </Badge>
      <span className="text-zinc-500 shrink-0 w-24 truncate" title={entry.source}>
        [{entry.source}]
      </span>
      <span className={cn('flex-1 break-all', styles.text)}>{entry.message}</span>
    </div>
  );
}

export default function LogViewer({
  logs,
  isConnected,
  compact = false,
  onClear,
  maxHeight = compact ? '150px' : '400px',
}: LogViewerProps) {
  const [autoScroll, setAutoScroll] = useState(true);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const bottomRef = useRef<HTMLDivElement>(null);

  // Auto-scroll when new logs arrive (if enabled)
  useEffect(() => {
    if (autoScroll && bottomRef.current) {
      bottomRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [logs.length, autoScroll]);

  // Detect manual scroll to disable auto-scroll
  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const container = e.currentTarget;
    const isAtBottom = container.scrollHeight - container.scrollTop - container.clientHeight < 50;
    setAutoScroll(isAtBottom);
  };

  // Download logs as text file
  const downloadLogs = useCallback(() => {
    const content = logs
      .map((e) => {
        const date = new Date(e.timestampUnixMs).toISOString();
        return `[${date}] [${e.level.toUpperCase().padEnd(5)}] [${e.source}] ${e.message}`;
      })
      .join('\n');

    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `edgelink-logs-${new Date().toISOString().slice(0, 19).replace(/:/g, '-')}.txt`;
    a.click();
    URL.revokeObjectURL(url);
  }, [logs]);

  return (
    <div className="space-y-2">
      {/* Controls bar */}
      <div className={cn('flex items-center justify-between', compact ? 'text-xs' : '')}>
        <div className="flex items-center gap-2">
          {isConnected ? (
            <Badge
              variant="outline"
              className={cn(
                'bg-lime/10 border-lime/30 text-lime gap-1',
                compact && 'text-[10px] px-1.5 py-0'
              )}
            >
              <Wifi className={cn(compact ? 'h-2.5 w-2.5' : 'h-3 w-3')} />
              {!compact && 'Connected'}
            </Badge>
          ) : (
            <Badge
              variant="outline"
              className={cn(
                'bg-zinc-700/20 border-zinc-600/30 text-zinc-400 gap-1',
                compact && 'text-[10px] px-1.5 py-0'
              )}
            >
              <WifiOff className={cn(compact ? 'h-2.5 w-2.5' : 'h-3 w-3')} />
              {!compact && 'Disconnected'}
            </Badge>
          )}
          <span className="text-muted-foreground">{logs.length} entries</span>
        </div>

        <div className="flex items-center gap-1">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setAutoScroll(!autoScroll)}
            className={cn(
              'gap-1',
              compact ? 'h-6 px-2 text-xs' : 'h-7',
              autoScroll && 'text-lime'
            )}
          >
            {autoScroll ? (
              <ArrowDown className={cn(compact ? 'h-3 w-3' : 'h-3.5 w-3.5')} />
            ) : (
              <Pause className={cn(compact ? 'h-3 w-3' : 'h-3.5 w-3.5')} />
            )}
            {!compact && (autoScroll ? 'Auto-scroll' : 'Paused')}
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={downloadLogs}
            disabled={logs.length === 0}
            className={cn('gap-1', compact ? 'h-6 px-2 text-xs' : 'h-7')}
          >
            <Download className={cn(compact ? 'h-3 w-3' : 'h-3.5 w-3.5')} />
            {!compact && 'Download'}
          </Button>
          {onClear && (
            <Button
              variant="ghost"
              size="sm"
              onClick={onClear}
              className={cn('gap-1', compact ? 'h-6 px-2 text-xs' : 'h-7')}
            >
              <Trash2 className={cn(compact ? 'h-3 w-3' : 'h-3.5 w-3.5')} />
              {!compact && 'Clear'}
            </Button>
          )}
        </div>
      </div>

      {/* Log viewer */}
      <Card className="bg-zinc-950/80 border-zinc-800">
        <CardContent className="p-0">
          {logs.length === 0 ? (
            <div
              className={cn(
                'flex items-center justify-center text-center text-muted-foreground',
                compact ? 'py-4 text-xs' : 'py-8'
              )}
            >
              <Terminal className={cn('mr-2', compact ? 'h-4 w-4' : 'h-5 w-5')} />
              {isConnected ? 'Waiting for logs...' : 'Disconnected'}
            </div>
          ) : (
            <div
              ref={scrollContainerRef}
              className="overflow-y-auto overflow-x-hidden"
              style={{ maxHeight }}
              onScroll={handleScroll}
            >
              <div className={cn(compact ? 'p-2' : 'p-2')}>
                {logs.map((entry, index) => (
                  <LogEntryRow
                    key={`${entry.timestampUnixMs}-${index}`}
                    entry={entry}
                    compact={compact}
                  />
                ))}
                <div ref={bottomRef} />
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Auto-scroll indicator when paused */}
      {!autoScroll && logs.length > 0 && !compact && (
        <div className="flex justify-center">
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              setAutoScroll(true);
              bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
            }}
            className="gap-2"
          >
            <Play className="h-3.5 w-3.5" />
            Resume auto-scroll
          </Button>
        </div>
      )}
    </div>
  );
}
